import { Graphics } from "pixi.js"
import { TileSize } from "game-common/models"
import type { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { FLOOR_RIM_INSET } from "./floor_art"

export type OpeningEdgeSide = "north" | "east" | "south" | "west"
export interface OpeningEdge { x: number; y: number; side: OpeningEdgeSide }

type OpeningMap = Pick<TileMapMetaProvider, "twoStoreyHouse" | "resolveTileMapFromLayerId" |
    "isFloorOpeningAt" | "isHiddenTile" | "isStairAt">

/** Whether a cell shows floor art an opening can have a rim against (not another opening, not a stair). */
const hasFloorArt = (provider: OpeningMap, x: number, y: number): boolean => {
    const tile = provider.resolveTileMapFromLayerId("map2")?.[x]?.[y]
    return !!tile && tile !== "0" && !provider.isHiddenTile(tile) && !provider.isFloorOpeningAt(x, y) && !provider.isStairAt(x, y)
}

/** Only edges shared by an explicit opening and visible floor art need a rim.
 * Adjacent openings contribute no shared edge; outside space and stairs aren't holes. */
export function floorOpeningEdges(provider: OpeningMap): OpeningEdge[] {
    if (!(provider.twoStoreyHouse?.floor > 0)) return []
    const markers = provider.resolveTileMapFromLayerId("sprite2")
    const edges: OpeningEdge[] = []
    const neighbors: [OpeningEdgeSide, number, number][] = [
        ["north", 0, -1], ["east", 1, 0], ["south", 0, 1], ["west", -1, 0],
    ]
    markers?.forEach((column, x) => column?.forEach((marker, y) => {
        if (!marker || marker === "0" || !provider.isFloorOpeningAt(x, y)) return
        for (const [side, dx, dy] of neighbors) {
            if (hasFloorArt(provider, x + dx, y + dy)) edges.push({ x, y, side })
        }
    }))
    return edges
}

/** Crisp depth cues stay visible independently of the sun and inherit their floor's projection.
 * The floor art stops FLOOR_RIM_INSET short of an opening (floor_art.ts), so the hole you see is the
 * opening's cells widened by that much wherever floor runs up to them: every mark sits along that
 * visible edge, inside the hole, leaving the floor artwork intact. */
export class FloorOpeningEdges extends Graphics {
    private provider?: TileMapMetaProvider
    private version?: string
    private floorAt: (x: number, y: number) => boolean = () => false

    constructor() {
        super()
        this.interactive = false
        // Tilemaps draw at zero, stairs and bodies above this ground decoration.
        this.zIndex = 0.5
    }

    sync(provider?: TileMapMetaProvider): void {
        const version = provider && `${provider.getPresentationVersion()}:${provider.twoStoreyHouse?.floor}`
        if (this.provider === provider && this.version === version) return
        this.provider = provider
        this.version = version
        this.clear()
        const edges = provider ? floorOpeningEdges(provider) : []
        this.visible = edges.length > 0
        this.floorAt = (x, y) => !!provider && hasFloorArt(provider, x, y)
        // Two bands of ambient shade, independent of directional building shadows.
        for (const edge of edges) {
            this.band(edge, edge.side === "north" ? 7 : 4, 0x171410, 0.12)
            this.band(edge, edge.side === "north" ? 5 : 2, 0x171410, 0.18)
        }
        // Looking down exposes the face of the floor along the far (north) edge.
        for (const edge of edges) {
            if (edge.side === "north") this.band(edge, 3, 0x4a4037, 0.95)
        }
        for (const edge of edges) this.band(edge, 1, 0x171410, 0.88)
    }

    /** A band along an edge of the visible hole: past the opening's cell by the rim inset, and on past
     * its ends wherever the floor beside it is inset too (where an opening is, the edge carries on there). */
    private band(edge: OpeningEdge, depth: number, color: number, alpha: number): void {
        const I = FLOOR_RIM_INSET
        const x0 = edge.x * TileSize, y0 = edge.y * TileSize, x1 = x0 + TileSize, y1 = y0 + TileSize
        const across = edge.side === "north" || edge.side === "south"
        const from = across ? x0 - (this.floorAt(edge.x - 1, edge.y) ? I : 0) : y0 - (this.floorAt(edge.x, edge.y - 1) ? I : 0)
        const to = across ? x1 + (this.floorAt(edge.x + 1, edge.y) ? I : 0) : y1 + (this.floorAt(edge.x, edge.y + 1) ? I : 0)
        this.beginFill(color, alpha)
        switch (edge.side) {
            case "north": this.drawRect(from, y0 - I, to - from, depth); break
            case "east": this.drawRect(x1 + I - depth, from, depth, to - from); break
            case "south": this.drawRect(from, y1 + I - depth, to - from, depth); break
            case "west": this.drawRect(x0 - I, from, depth, to - from); break
        }
        this.endFill()
    }
}
